/**
 * Shared submit pipeline for website forms.
 */

import { useCallback, type Dispatch, type SetStateAction } from "react";
import { toast } from "sonner";
import {
  validateAndPrepareWebsiteForm,
  type WebsiteFormFieldErrors,
  type WebsiteFormValidationOptions,
} from "@/utils/websiteFormUtils";

type WebsiteFormSubmitArgs = {
  t: (key: string) => string;
  isSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
  setShowValidation: (value: boolean) => void;
  setFieldErrors: Dispatch<SetStateAction<WebsiteFormFieldErrors>>;
  setUrlErrorKey: Dispatch<SetStateAction<string | null>>;
};

type WebsiteFormSubmitRequest = {
  formData: WebsiteFormValidationOptions["formData"];
  validationOptions: WebsiteFormValidationOptions;
  onValidSubmit: (normalizedUrl: string) => Promise<void>;
};

export const useWebsiteFormSubmit = ({
  t,
  isSubmitting,
  setIsSubmitting,
  setShowValidation,
  setFieldErrors,
  setUrlErrorKey,
}: WebsiteFormSubmitArgs) => {
  return useCallback(
    async ({
      formData,
      validationOptions,
      onValidSubmit,
    }: WebsiteFormSubmitRequest) => {
      setShowValidation(true);
      setUrlErrorKey(null);

      if (isSubmitting) return false;
      setIsSubmitting(true);

      try {
        const validation = await validateAndPrepareWebsiteForm(validationOptions);

        setFieldErrors(validation.fieldErrors);
        setUrlErrorKey(validation.urlErrorKey ?? null);

        if (!validation.ok) {
          validation.errors.forEach((error) => {
            const message =
              typeof error.max === "number"
                ? t(error.messageKey).replace("{max}", String(error.max))
                : t(error.messageKey);
            toast.error(message);
          });

          if (
            validation.firstErrorId &&
            validation.urlErrorKey !==
              validationOptions.errorKeys.urlVerifyFailed
          ) {
            document
              .getElementById(validation.firstErrorId)
              ?.scrollIntoView({ behavior: "smooth", block: "center" });
          }

          return false;
        }

        const normalizedUrl =
          validation.normalizedUrl ?? formData.url.trim();

        await onValidSubmit(normalizedUrl);
        return true;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isSubmitting,
      setFieldErrors,
      setIsSubmitting,
      setShowValidation,
      setUrlErrorKey,
      t,
    ]
  );
};
